// EUDI Wallet vocabularies.
//
// Primary: `EUDI_READINESS_STAGES` — organisational integration readiness ("can
// an organisation integrate with this wallet today?"). It drives the card row,
// the "EUDI Wallets" globe overlay, the table filter and the map legend. Array
// order doubles as the legend order and the precedence used when a country has
// several EUDI entries (highest first).
//
// Readiness is deliberately NOT domestic app availability, citizen rollout or
// pilots, certification, or Persona support (`personaCoverage` / `status`).
//
// Legacy: `EUDI_WALLET_STATUSES` / `eudiWalletStatus` predate the readiness
// stages. They are kept for CSV/API consumers and as supporting evidence (the
// "Existing app — …" values feed the card's EXISTING APP UPGRADE row), and are
// the fallback a readiness stage is derived from when an entry has no reviewed
// `eudiReadiness`.
import type {
  DigitalIdCoverageEntry,
  DigitalIdsByRegion,
  EudiReadiness,
  EudiReadinessAccess,
  EudiReadinessStage,
} from '../types'
import type { StickerVariant } from './stickers'

import { colors } from './colors'

export interface EudiReadinessStageToken {
  /** Swatch + globe fill color (token from colors.ts). */
  color: string
  /** Slug used in CSS class names and DOM data attributes. */
  slug: string
  /** Readiness stage value (`eudiReadiness.stage`). */
  status: EudiReadinessStage
  /** Sticker variant matching `color`, used on the card. */
  stickerVariant: StickerVariant
}

export interface EudiWalletStatusToken {
  /** Swatch + globe fill color (token from colors.ts). */
  color: string
  /** Slug used in CSS class names and DOM data attributes. */
  slug: string
  /** Value stored in `eudiWalletStatus` on a digital-ID coverage entry. */
  status: string
}

/** `idType` values that carry EUDI Wallet readiness / legacy status. */
export const EUDI_WALLET_ID_TYPES = ['EUDI Wallet', 'National eID + EUDI Wallet']

/**
 * Organisational integration readiness stages, highest precedence first. This
 * order is the legend order and the precedence used for map fill.
 */
export const EUDI_READINESS_STAGES: EudiReadinessStageToken[] = [
  {
    color: colors.stickerGreen,
    slug: 'production-available',
    status: 'Production available',
    stickerVariant: 'green',
  },
  {
    color: colors.stickerBlue,
    slug: 'sandbox-available',
    status: 'Sandbox available',
    stickerVariant: 'blue',
  },
  {
    color: colors.stickerYellow,
    slug: 'in-development',
    status: 'In development',
    stickerVariant: 'yellow',
  },
  {
    color: colors.stickerOrange,
    slug: 'announced',
    status: 'Announced',
    stickerVariant: 'orange',
  },
  {
    color: colors.stickerGrey,
    slug: 'not-confirmed',
    status: 'Not confirmed',
    stickerVariant: 'grey',
  },
]

/** How an organisation gets access to the wallet's integration environment. */
export const EUDI_READINESS_ACCESS_VALUES: EudiReadinessAccess[] = [
  'Self-service',
  'Application required',
  'Invitation only',
  'Not confirmed',
]

/**
 * Legacy EUDI Wallet rollout statuses (`eudiWalletStatus`). Kept for CSV/API
 * compatibility and as supporting evidence; the UI uses readiness stages.
 */
export const EUDI_WALLET_STATUSES: EudiWalletStatusToken[] = [
  {
    color: colors.stickerGreen,
    slug: 'announced-sandbox',
    status: 'Sandbox available',
  },
  {
    color: colors.stickerBlue,
    slug: 'upgrade-confirmed',
    status: 'Existing app — upgrade confirmed',
  },
  {
    color: colors.stickerYellow,
    slug: 'repo-available',
    status: 'Repo only',
  },
  {
    color: colors.stickerOrange,
    slug: 'announced-no-sandbox',
    status: 'Announced',
  },
  {
    color: colors.stickerGrey,
    slug: 'upgrade-not-confirmed',
    status: 'Existing app — upgrade unconfirmed',
  },
]

/** Legacy statuses describing an upgrade of an existing domestic app. */
export const EUDI_EXISTING_APP_STATUSES = [
  'Existing app — upgrade confirmed',
  'Existing app — upgrade unconfirmed',
]

/**
 * Legacy status → derived readiness stage. A public government implementation
 * repo is evidence of development; an existing-app upgrade says nothing about
 * organisational integration beyond the announcement.
 */
export const EUDI_LEGACY_STATUS_TO_READINESS: Record<string, EudiReadinessStage> = {
  Announced: 'Announced',
  'Existing app — upgrade confirmed': 'Announced',
  'Existing app — upgrade unconfirmed': 'Not confirmed',
  'Repo only': 'In development',
  'Sandbox available': 'Sandbox available',
}

/** A readiness entry older than this many days (by `lastVerified`) is review-overdue. */
export const EUDI_READINESS_REVIEW_MAX_AGE_DAYS = 30

export type EudiReadinessFreshness = 'current' | 'review-overdue' | 'unverified'

/** Readiness as displayed: explicit (reviewed) or derived from the legacy status. */
export interface ResolvedEudiReadiness extends Omit<EudiReadiness, 'sourceUrl'> {
  /** True only when the stage comes from an explicit `eudiReadiness.stage`. */
  reviewed: boolean
  /** Primary evidence; absent on derived stages. */
  sourceUrl?: string
}

const STATUS_TOKENS = new Map(EUDI_WALLET_STATUSES.map((token) => [token.status, token]))
const STAGE_TOKENS = new Map<string, EudiReadinessStageToken>(
  EUDI_READINESS_STAGES.map((token) => [token.status, token])
)

export function getEudiReadinessStageToken(stage?: string): EudiReadinessStageToken | undefined {
  return stage == null ? undefined : STAGE_TOKENS.get(stage)
}

/** Legacy status token lookup. */
export function getEudiWalletStatusToken(status?: string): EudiWalletStatusToken | undefined {
  return status == null ? undefined : STATUS_TOKENS.get(status)
}

/** Legacy status of an existing domestic app being upgraded (supporting detail). */
export function isEudiExistingAppStatus(status?: string): boolean {
  return status != null && EUDI_EXISTING_APP_STATUSES.includes(status)
}

export function isEudiReadinessStage(stage?: string): stage is EudiReadinessStage {
  return stage != null && STAGE_TOKENS.has(stage)
}

export function isEudiWalletIdType(idType?: string): boolean {
  return idType != null && EUDI_WALLET_ID_TYPES.includes(idType)
}

/**
 * Resolve the readiness of an EUDI entry. An explicit `eudiReadiness.stage`
 * wins (`reviewed: true`); otherwise the stage is derived from the legacy
 * `eudiWalletStatus` (`reviewed: false`), and missing/unknown → 'Not confirmed'.
 *
 * Dates (launchDate, evidenceDate, planned milestones) are never consulted: a
 * stage is never promoted because a date has passed or is approaching.
 *
 * Returns undefined for entries whose `idType` is not an EUDI Wallet type.
 */
export function resolveEudiReadiness(
  entry: Pick<DigitalIdCoverageEntry, 'eudiReadiness' | 'eudiWalletStatus' | 'idType'>
): ResolvedEudiReadiness | undefined {
  if (!isEudiWalletIdType(entry.idType)) return undefined

  const explicit = entry.eudiReadiness
  if (explicit != null && isEudiReadinessStage(explicit.stage)) {
    return { ...explicit, reviewed: true }
  }

  const legacy = entry.eudiWalletStatus
  const stage =
    (legacy != null ? EUDI_LEGACY_STATUS_TO_READINESS[legacy] : undefined) ?? 'Not confirmed'
  return { reviewed: false, stage }
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const DAY_MS = 24 * 60 * 60 * 1000

/** Resolved readiness summary exposed alongside raw `eudiReadiness` in the JSON API. */
export interface EudiReadinessApiSummary {
  freshness: EudiReadinessFreshness
  reviewed: boolean
  stage: EudiReadinessStage
}

/**
 * Freshness of a resolved readiness entry relative to `now` (injectable for
 * tests). Derived stages, and a missing, invalid or future `lastVerified`, are
 * 'unverified'; more than EUDI_READINESS_REVIEW_MAX_AGE_DAYS days old is
 * 'review-overdue'; otherwise 'current'.
 */
export function getEudiReadinessFreshness(
  readiness: Pick<ResolvedEudiReadiness, 'lastVerified' | 'reviewed'>,
  now: Date
): EudiReadinessFreshness {
  if (!readiness.reviewed) return 'unverified'
  const verified = parseIsoDate(readiness.lastVerified)
  if (verified == null) return 'unverified'
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const ageDays = Math.round((today - verified) / DAY_MS)
  if (ageDays < 0) return 'unverified'
  if (ageDays > EUDI_READINESS_REVIEW_MAX_AGE_DAYS) return 'review-overdue'
  return 'current'
}

/** Parse a YYYY-MM-DD date to a UTC midnight timestamp, or undefined if invalid. */
export function parseIsoDate(value?: string): number | undefined {
  if (value == null) return undefined
  const match = ISO_DATE.exec(value)
  if (match == null) return undefined
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const time = Date.UTC(year, month - 1, day)
  const date = new Date(time)
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return undefined
  }
  return time
}

/** Readiness stages in precedence order, so the first entry wins when a country has several. */
export function sortEudiReadinessStages(stages: string[]): EudiReadinessStage[] {
  return EUDI_READINESS_STAGES.filter(({ status }) => stages.includes(status)).map(
    ({ status }) => status
  )
}

/** Legacy statuses in legend order, so the first entry wins when a country has several. */
export function sortEudiWalletStatuses(statuses: string[]): string[] {
  return EUDI_WALLET_STATUSES.filter(({ status }) => statuses.includes(status)).map(
    ({ status }) => status
  )
}

/**
 * Copy of a region with `eudiReadinessResolved` (resolved stage, reviewed flag
 * and freshness) added to each EUDI entry. Raw `eudiReadiness` and the legacy
 * `eudiWalletStatus` pass through unchanged.
 */
export function withResolvedEudiReadiness(
  region: DigitalIdsByRegion,
  now: Date
): DigitalIdsByRegion {
  return {
    ...region,
    digitalIds: region.digitalIds.map((entry) => {
      const readiness = resolveEudiReadiness(entry)
      if (readiness == null) return entry
      const eudiReadinessResolved: EudiReadinessApiSummary = {
        freshness: getEudiReadinessFreshness(readiness, now),
        reviewed: readiness.reviewed,
        stage: readiness.stage,
      }
      return { ...entry, eudiReadinessResolved }
    }),
  }
}
